import { FIGURE_FICHES, type FigureId } from './figures.js';
import { RULE_FICHES } from './rules.js';
import { SYNC_FICHES } from './syncs.js';
import type { GuideFiche } from './types.js';

export * from './types.js';
export * from './figures.js';
export * from './rules.js';
export * from './syncs.js';
export * from './coverage.js';
export * from './leaves.js';

/** Le Guide du Cockpit : chiffres, règles de cartes, synchros (lot AD). */
export const GUIDE: readonly GuideFiche[] = [...FIGURE_FICHES, ...RULE_FICHES, ...SYNC_FICHES];

const BY_ID = new Map<string, GuideFiche>(GUIDE.map((f) => [f.id, f]));
const FIGURE_IDS = new Set<string>(FIGURE_FICHES.map((f) => f.id));

export function guideFiche(id: string): GuideFiche | null {
  return BY_ID.get(id) ?? null;
}

export function isFigureId(id: unknown): id is FigureId {
  return typeof id === 'string' && FIGURE_IDS.has(id);
}

/** Question préremplie du bouton « Comment c'est calculé ? ». */
export function figureQuestion(id: FigureId): string {
  const fiche = BY_ID.get(id);
  return `Comment est calculé « ${fiche?.name ?? id} » ?`;
}

/** Minuscules sans accents ni ponctuation (recherche dans le guide). */
export function normalizeGuideText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[’']/g, ' ')
    .replace(/[^a-z0-9.+]+/g, ' ')
    .trim();
}

const STOP_WORDS = new Set([
  'le',
  'la',
  'les',
  'de',
  'des',
  'du',
  'd',
  'l',
  'un',
  'une',
  'et',
  'ou',
  'a',
  'au',
  'aux',
  'en',
  'ce',
  'cette',
  'est',
  'comment',
  'calcule',
  'calcul',
  'pourquoi',
  'quoi',
  'que',
  'qui',
  'mon',
  'ma',
  'mes',
  'chiffre',
  'carte',
  'tuile',
  'j',
  'ai',
  'il',
  'y',
]);

function words(text: string): string[] {
  return normalizeGuideText(text)
    .split(/[\s.]+/)
    .filter((w) => w.length > 1 && !STOP_WORDS.has(w));
}

/**
 * Fiches qui correspondent à un sujet : l'identifiant exact (ou la règle `ruleKey`), sinon les fiches dont le
 * nom, l'identifiant ou le résumé partagent le plus de mots avec le sujet (au plus `limit`, les meilleures en
 * premier). Logique pure, testée.
 */
export function searchGuide(topic: string, limit = 5): GuideFiche[] {
  const raw = topic.trim();
  const exact = BY_ID.get(raw) ?? GUIDE.find((f) => f.ruleKey === raw);
  if (exact) return [exact];
  const wanted = words(raw);
  if (wanted.length === 0) return [];
  const scored = GUIDE.map((fiche) => {
    const name = new Set(words(`${fiche.name} ${fiche.id.replace(/[-_]/g, ' ')}`));
    const summary = new Set(words(fiche.summary));
    let score = 0;
    for (const w of wanted) {
      if (name.has(w)) score += 3;
      else if ([...name].some((n) => n.length >= 4 && w.length >= 4 && (n.startsWith(w) || w.startsWith(n))))
        score += 2;
      else if (summary.has(w)) score += 1;
    }
    return { fiche, score };
  })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || a.fiche.id.localeCompare(b.fiche.id));
  return scored.slice(0, limit).map((s) => s.fiche);
}

/** Index du guide pour la consigne de l'assistant : une ligne par fiche (stable : mis en cache). */
export function guideIndex(): string {
  const section = (kind: GuideFiche['kind'], title: string) =>
    `${title}\n${GUIDE.filter((f) => f.kind === kind)
      .map((f) => `- ${f.id} : ${f.name} (${f.screens.join(', ')})`)
      .join('\n')}`;
  return [
    section('figure', 'Chiffres (explain_figure accepte ces identifiants) :'),
    section('rule', 'Règles des cartes « À faire » :'),
    section('sync', 'Synchros :'),
  ].join('\n\n');
}
